import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchAuthSession, signIn, signOut, getCurrentUser } from 'aws-amplify/auth';
import { profileApi } from '../services/api';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import { SessionTimeoutModal } from '../components/auth/SessionTimeoutModal';
import { SESSION_CONFIG } from '../config/session';

export type UserRole = 'superadmin' | 'admin' | 'colaborador';

export interface User {
  id: string;
  userId: string; // Alias for id
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  companyId: string;
  companyName?: string;
  tradeName?: string;
  avatarUrl: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ requiresNewPassword: boolean }>;
  logout: () => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      
      if (currentUser && session.tokens) {
        const idToken = session.tokens.idToken;
        const payload = idToken?.payload;
        
        // Extract first and last name with fallbacks
        let firstName = payload?.given_name as string || '';
        let lastName = payload?.family_name as string || '';
        
        // Fallback: if no given_name, try to split 'name' attribute
        if (!firstName && payload?.name) {
          const nameParts = (payload.name as string).split(' ');
          firstName = nameParts[0] || '';
          lastName = nameParts.slice(1).join(' ') || '';
        }
        
        // Final fallback: use email username
        if (!firstName) {
          firstName = (payload?.email as string || '').split('@')[0];
        }
        
        const role = (payload?.['custom:role'] as UserRole) || 'colaborador';
        
        setUser({
          id: currentUser.userId,
          userId: currentUser.userId, // Alias for compatibility
          email: payload?.email as string || '',
          firstName,
          lastName,
          role,
          companyId: payload?.['custom:company_id'] as string || '',
          avatarUrl: null,
        });
        
        // Superadmin no tiene empresa real, no intentar cargar perfil del backend
        if (role !== 'superadmin') {
          try {
            const profile = await profileApi.getProfile();
            setUser((prev) =>
              prev
                ? {
                    ...prev,
                    firstName: profile.firstName,
                    lastName: profile.lastName,
                    companyName: profile.companyName,
                    tradeName: profile.tradeName,
                    avatarUrl: profile.avatarUrl ?? null,
                  }
                : prev
            );
          } catch (error) {
            console.error('Error loading profile:', error);
          }
        }
      }
    } catch (error) {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<{ requiresNewPassword: boolean }> => {
    try {
      // Limpiar sesión local residual si quedó una sesión colgada
      try {
        await getCurrentUser();
        await signOut();
      } catch {
        // No hay sesión activa, continuar normalmente
      }

      const result = await signIn({ username: email, password });
      
      // Check if Cognito requires a new password (first login with temp password)
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return { requiresNewPassword: true };
      }
      
      if (result.isSignedIn) {
        await checkAuthStatus();
      }
      return { requiresNewPassword: false };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const hasRole = (role: UserRole): boolean => {
    return user?.role === role;
  };

  const refreshUser = useCallback(async () => {
    // Superadmin no tiene perfil en backend
    if (user?.role === 'superadmin') return;
    try {
      const profile = await profileApi.getProfile();
      setUser((prev) =>
        prev
          ? {
              ...prev,
              firstName: profile.firstName,
              lastName: profile.lastName,
              companyName: profile.companyName,
              tradeName: profile.tradeName,
              avatarUrl: profile.avatarUrl ?? null,
            }
          : prev
      );
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    }
  }, [user?.role]);

  // Hook de timeout de inactividad
  const { isWarning, remainingTime, extendSession } = useIdleTimeout({
    idleTime: SESSION_CONFIG.IDLE_TIME,
    warningTime: SESSION_CONFIG.WARNING_TIME,
    onIdle: () => {
      logout();
    },
    onWarning: () => {
      // Advertencia mostrada
    },
    enabled: !!user && !isLoading, // Solo activar si hay usuario autenticado
  });

  // Log inicial del sistema


  const handleExtendSession = () => {
    extendSession();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        hasRole,
        refreshUser,
      }}
    >
      {children}
      
      {/* Modal de advertencia de timeout */}
      <SessionTimeoutModal
        isOpen={isWarning}
        remainingTime={remainingTime}
        onExtendSession={handleExtendSession}
        onLogout={logout}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
